import { useId, useState } from 'react';
import { cn } from 'utils/cn';

interface Props {
    value: string;
    onChange: (value: string) => void;
    /** Runs with the code as soon as it is complete, typed, pasted or autofilled, so the user need not press Continue. */
    onComplete?: (code: string) => void;
    error?: boolean;
    disabled?: boolean;
    autoFocus?: boolean;
    /** Bumping it remounts the input, which clears it and, with `autoFocus`, puts the focus back on it. */
    resetKey?: number;
    ariaLabel: string;
}

/** One stable name per box; the boxes never reorder. */
const BOX_SLOTS = ['d1', 'd2', 'd3', 'd4', 'd5', 'd6'] as const;

export const MFA_CODE_LENGTH = BOX_SLOTS.length;

/**
 * A TOTP code shown as six one-digit boxes.
 *
 * <p>One real input holds the whole code and the boxes only draw its value. Password managers and the
 * browser fill a one-time code into a single field they can recognise (`autocomplete="one-time-code"`),
 * which six one-character inputs never offer. The input covers the boxes with transparent text rather than
 * being hidden: a manager skips fields that CSS hides, `opacity: 0` included.
 */
export function MfaCodeInput({ resetKey = 0, ...props }: Props) {
    return <CodeBoxes key={resetKey} {...props} />;
}

function CodeBoxes({
    value,
    onChange,
    onComplete,
    error = false,
    disabled = false,
    autoFocus = true,
    ariaLabel,
}: Omit<Props, 'resetKey'>) {
    const id = useId();
    const [focused, setFocused] = useState(false);
    // The caret is invisible, so the highlighted box stands in for it: an edit lands where the caret is,
    // not only at the end. Without a known caret the next empty box is the one.
    const [caret, setCaret] = useState<number | null>(null);
    const activeIndex = Math.min(caret ?? value.length, value.length, MFA_CODE_LENGTH - 1);

    return (
        <div className="relative flex justify-between gap-2.5">
            {BOX_SLOTS.map((slot, index) => (
                <div
                    key={slot}
                    aria-hidden
                    className={cn(
                        'bg-card text-foreground flex h-14 min-w-0 flex-1 items-center justify-center rounded-[10px] border text-[22px] font-semibold transition-[border-color,box-shadow]',
                        error ? 'border-destructive' : 'border-input',
                        focused && index === activeIndex && 'border-primary ring-ring/20 ring-3',
                        disabled && 'opacity-60'
                    )}
                >
                    {value[index] ?? ''}
                </div>
            ))}
            <input
                id={id}
                name="one-time-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                // oxlint-disable-next-line jsx-a11y/no-autofocus -- the code is the only thing on the screen to fill
                autoFocus={autoFocus}
                value={value}
                disabled={disabled}
                aria-label={ariaLabel}
                aria-invalid={error}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onSelect={(event) => setCaret(event.currentTarget.selectionStart)}
                onMouseUp={(event) => {
                    // The input's own text is small and sits at the left edge, so where the browser drops the caret
                    // on a click has nothing to do with the box under the pointer. The box is taken from the
                    // pointer instead: a filled box selects its digit so the next key replaces it, an empty box
                    // puts the caret after the code.
                    const target = event.currentTarget;
                    const rect = target.getBoundingClientRect();
                    if (rect.width === 0) {
                        return;
                    }
                    const box = Math.floor(((event.clientX - rect.left) / rect.width) * MFA_CODE_LENGTH);
                    const index = Math.max(0, Math.min(box, MFA_CODE_LENGTH - 1));
                    if (index < value.length) {
                        target.setSelectionRange(index, index + 1);
                    } else {
                        target.setSelectionRange(value.length, value.length);
                    }
                }}
                onChange={(event) => {
                    // Autofill and paste can bring spaces or a dash ("123 456"); only the digits count. The length
                    // limit lives here, not in `maxLength`: a browser cuts a pasted value to that length before this runs.
                    const next = event.target.value.replace(/\D/g, '').slice(0, MFA_CODE_LENGTH);
                    // A digit filtered out moves the caret to the end; the next select event reports it again.
                    setCaret(null);
                    onChange(next);
                    if (next.length === MFA_CODE_LENGTH && next !== value) {
                        onComplete?.(next);
                    }
                }}
                className="absolute inset-0 size-full cursor-text border-0 bg-transparent text-base text-transparent caret-transparent outline-none selection:bg-transparent disabled:cursor-default"
            />
        </div>
    );
}
